// @ts-nocheck
import * as React from 'react';
import {
  Sheet,
  SheetContent,
} from '@frontend/shadcn/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@frontend/shadcn/components/ui/tooltip';
import { Alert, AlertDescription } from '@frontend/shadcn/components/ui/alert';
import { Progress } from '@frontend/shadcn/components/ui/progress';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { Button } from '@revfluence/fresh';
import {
  CircleInfoIcon,
  ArrowUpFromLineIcon,
  CheckIcon,
  XmarkIcon,
  TriangleExclamationIcon,
  PlusIcon,
  DownloadIcon
} from '@revfluence/fresh-icons/solid/esm';

import { useGetCustomStageTaskDetails } from '@frontend/app/hooks';
import { useUpdateCustomStageTaskDetails } from '@frontend/app/hooks/useUpdateCustomStageTaskDetails';
import { FieldSchemaType } from '@frontend/app/types/globalTypes';
import { NameCell } from '@frontend/app/refresh-components';
import { useState } from 'react';
import { useUploadContent } from '@frontend/app/hooks';
import { SingleSelect } from './SingleSelect';
import { MultiSelect } from './MultiSelect';

interface UploadFieldState {
  file: File | null;
  status: 'idle' | 'getting-url' | 'ready' | 'uploading' | 'success' | 'error';
  progress: number;
  uploadedUrl: string | null;
  errorMessage: string | null;
  fileName: string | null;
}

interface UploadStates {
  [fieldId: string]: UploadFieldState;
}

interface CustomStageFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  member: {
    name: string;
    profilePicture?: string;
  };
  stageId: number;
  programId: number;
  memberId: number;
  refetchData?: () => Promise<void>;
}

export const CustomStageFormDrawer: React.FC<CustomStageFormDrawerProps> = ({
  isOpen,
  onClose,
  member,
  stageId,
  programId,
  memberId,
  refetchData,
}) => {
  const { stageTaskDetails, loading } = useGetCustomStageTaskDetails({
    programId,
    stageId,
    memberId,
  });

  const { updateStageTaskDetails, loading: updateLoading, error: updateError } = useUpdateCustomStageTaskDetails();

  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [uploadStates, setUploadStates] = useState<UploadStates>({});

  // Upload hook for file uploads
  const { upload } = useUploadContent({
    isTemp: false,
    keepFileName: true,
    serviceName: 'program',
  });

  // Upload state management functions
  const initializeUploadState = (_fieldId: string): UploadFieldState => ({
    file: null,
    status: 'idle',
    progress: 0,
    uploadedUrl: null,
    errorMessage: null,
    fileName: null,
  });

  const updateUploadState = (fieldId: string, updates: Partial<UploadFieldState>) => {
    setUploadStates((prev) => ({
      ...prev,
      [fieldId]: { ...initializeUploadState(fieldId), ...prev[fieldId], ...updates }
    }));
  };

  const handleFileSelect = async (file: File, fieldId: string) => {
    try {
      // Initialize upload state
      updateUploadState(fieldId, {
        file,
        status: 'getting-url',
        errorMessage: null,
        progress: 0
      });

      // Upload file using the upload hook
      const fileUrl = await upload(file, 'application');

      // Update state to success
      updateUploadState(fieldId, {
        status: 'success',
        uploadedUrl: fileUrl,
        fileName: file.name,
        progress: 100
      });

      // Update form data with uploaded file URL
      handleInputChange(parseInt(fieldId, 10), fileUrl);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      updateUploadState(fieldId, {
        status: 'error',
        errorMessage
      });
    }
  };

  const removeFile = (fieldId: string) => {
    updateUploadState(fieldId, initializeUploadState(fieldId));
    handleInputChange(parseInt(fieldId, 10), '');
  };

  // Extract filename from file URL - handles two formats:
  // Format 1: bucket/uuid/filename -> get last segment
  // Format 2: bucket/filename/uuid/uuid -> get segment after bucket
  const extractFilenameFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathSegments = urlObj.pathname.split('/').filter((segment) => segment);

      // Remove the bucket name (first segment)
      const segmentsAfterBucket = pathSegments.slice(1);

      if (segmentsAfterBucket.length === 0) return 'unknown-file';

      // Check if the last segment looks like a filename (has extension)
      const lastSegment = segmentsAfterBucket[segmentsAfterBucket.length - 1];
      const hasExtension = lastSegment.includes('.') && !lastSegment.match(/^[a-f0-9-]+$/);

      if (hasExtension) {
        // Format 1: filename is at the end
        return lastSegment;
      } else {
        // Format 2: filename should be the first segment after bucket
        const firstSegment = segmentsAfterBucket[0];
        if (firstSegment && firstSegment.includes('.')) {
          return firstSegment;
        }
      }

      // Fallback: return the first segment that looks like a filename
      for (const segment of segmentsAfterBucket) {
        if (segment.includes('.') && !segment.match(/^[a-f0-9-]+$/)) {
          return segment;
        }
      }

      return 'file';
    } catch {
      return 'file';
    }
  };

  const handleDownloadFile = (fileUrl: string, fileName?: string) => {
    const extractedFileName = fileName || extractFilenameFromUrl(fileUrl);
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = extractedFileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Check if all mandatory fields are filled
  const areAllMandatoryFieldsFilled = React.useMemo(() => {
    if (!stageTaskDetails?.fieldSchemas) return false;

    return stageTaskDetails.fieldSchemas.every((schema) => {
      if (!schema || schema.type === FieldSchemaType.UPLOAD) return true; // Skip upload fields for now

      // If field is not mandatory, it's considered valid
      if (!schema.isMandatory) return true;

      const fieldValue = formValues[schema.id];

      // Check if the field has a value
      if (schema.type === FieldSchemaType.MULTI_SELECT) {
        return Array.isArray(fieldValue) && fieldValue.length > 0;
      } else if (schema.type === FieldSchemaType.SINGLE_SELECT || schema.type === FieldSchemaType.BOOLEAN) {
        return fieldValue && fieldValue !== '';
      } else {
        return fieldValue && fieldValue.trim() !== '';
      }
    });
  }, [stageTaskDetails?.fieldSchemas, formValues]);

  // Initialize form values from API response
  React.useEffect(() => {
    if (stageTaskDetails?.fieldSchemas) {
      const initialValues: Record<string, any> = {};
      const initialUploadStates: UploadStates = {};

      // Sort fields by fieldOrder before processing
      const sortedFieldSchemas = stageTaskDetails.fieldSchemas ? [...stageTaskDetails.fieldSchemas].sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0)) : [];

      sortedFieldSchemas.forEach((schema) => {
        if (!schema) return;

        const fieldValue = schema.fieldValues?.[0];

        if (schema.type === FieldSchemaType.UPLOAD) {
          // Handle upload fields - if there's already a file uploaded, show it as success
          if (fieldValue?.value) {
            initialValues[schema.id] = fieldValue.value;
            initialUploadStates[schema.id.toString()] = {
              file: null,
              status: 'success',
              progress: 100,
              uploadedUrl: fieldValue.value,
              errorMessage: null,
              fileName: extractFilenameFromUrl(fieldValue.value)
            };
          }
        } else if (schema.type === FieldSchemaType.MULTI_SELECT) {
          initialValues[schema.id] = fieldValue?.metadata?.value || [];
        } else if (schema.type === FieldSchemaType.SINGLE_SELECT) {
          initialValues[schema.id] = fieldValue?.metadata?.value?.[0] || '';
        } else {
          initialValues[schema.id] = fieldValue?.value || '';
        }
      });

      setFormValues(initialValues);
      setUploadStates(initialUploadStates);
    }
  }, [stageTaskDetails]);

  const handleInputChange = (fieldId: number, value: any) => {
    setFormValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  // Check if any uploads are in progress
  const hasActiveUploads = Object.values(uploadStates).some(
    (state) => state.status === 'getting-url' || state.status === 'uploading'
  );

  const handleSubmit = async () => {
    try {
      // Check if any uploads are still in progress
      if (hasActiveUploads) {
        return;
      }

      // Transform form values to the expected format
      const fields = Object.entries(formValues).map(([fieldId, value]) => {
        const schema = stageTaskDetails?.fieldSchemas?.find((s) => s.id === parseInt(fieldId, 10));

        if (schema?.type === FieldSchemaType.MULTI_SELECT || schema?.type === FieldSchemaType.SINGLE_SELECT) {
          // For select fields, put values in metadata
          return {
            fieldSchemaId: parseInt(fieldId, 10),
            value: null,
            metadata: {
              value: Array.isArray(value) ? value : [value]
            }
          };
        } else {
          // For text/date/upload fields, put in value
          return {
            fieldSchemaId: parseInt(fieldId, 10),
            value: value || null,
            metadata: null
          };
        }
      });

      await updateStageTaskDetails({
        data: {
          programId,
          stageId,
          memberId,
          fields
        }
      }, refetchData);

      // Close drawer on successful submission
      onClose();
    } catch (error) {
      console.error('Failed to update stage task details:', error);
      // Handle error (you might want to show a toast notification)
    }
  };

  if (loading) {
    // Add a loading state
    return <div />;
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[40vw] min-w-[480px] max-w-[720px] p-0 flex flex-col [&>button:first-of-type]:hidden">
        {/* Header - Fixed at top */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 inline-flex justify-center items-center gap-2">
          <div className="flex-1 justify-center text-zinc-900 text-base font-medium font-['Inter'] leading-7">
            {stageTaskDetails?.stage?.name}
          </div>
          <div className="flex justify-start items-center">
            <Button
              type="text"
              size="small"
              onClick={onClose}
            >
              <XmarkIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Member Info - Fixed below header */}
        <div className="flex-shrink-0 px-4 pb-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <NameCell
              imageUrl={member.profilePicture}
              name={member.name}
              showName={false}
            />
            {/* <Avatar src={member.profilePicture} fallback={member.name[0]} className="h-8 w-8" /> */}
            <span className="text-sm font-medium text-gray-800">{member.name}</span>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 flex flex-col gap-4">
            <div className="p-4 bg-white rounded-2xl border border-gray-200">
              <div className="flex flex-col gap-4">
                <p className="text-sm text-gray-800">
                  {stageTaskDetails?.name || ''}
                  {/* Please refer to our content best <a href="#" className="text-blue-600 underline">practices</a>. */}
                </p>

                {/* Dynamic Form Fields */}
                <div className="space-y-4">
                  {stageTaskDetails?.fieldSchemas ? [...stageTaskDetails.fieldSchemas].sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0)).map((schema) => {
                    if (!schema) return null;

                    // Handle Upload type field
                    if (schema.type === FieldSchemaType.UPLOAD) {
                      const fieldId = schema.id.toString();
                      const uploadState = uploadStates[fieldId] || initializeUploadState(fieldId);
                      const { file, status, progress, errorMessage } = uploadState;

                      const handleFileInput = (selectedFile: File) => {
                        handleFileSelect(selectedFile, fieldId);
                      };

                      const handleDrop = (e: React.DragEvent) => {
                        e.preventDefault();
                        const droppedFile = e.dataTransfer.files[0];
                        if (droppedFile) {
                          handleFileInput(droppedFile);
                        }
                      };

                      const handleDragOver = (e: React.DragEvent) => {
                        e.preventDefault();
                      };

                      return (
                        <div key={schema.id} className="space-y-2">
                          <div className="flex items-center gap-1">
                            <div className="text-zinc-900 text-xs font-medium">
                              {schema.name}{schema.isMandatory && <span className="text-red-400">*</span>}
                            </div>
                            <CircleInfoIcon className="h-3 w-3 text-gray-300" />
                          </div>

                          {status === 'success' && uploadState.uploadedUrl ? (
                            // Success state - show uploaded file
                            <Alert className="border-green-300 bg-green-50">
                              <CheckIcon className="h-4 w-4 text-green-600" />
                              <div className="flex items-center justify-between w-full">
                                <div className="flex-1">
                                  <AlertDescription className="text-sm font-medium text-gray-800">
                                    {extractFilenameFromUrl(uploadState.uploadedUrl)}
                                  </AlertDescription>
                                  <Badge variant="secondary" className="bg-green-100 text-green-700 mt-1">
                                    File uploaded successfully
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    type="text"
                                    size="small"
                                    onClick={() => handleDownloadFile(uploadState.uploadedUrl, extractFilenameFromUrl(uploadState.uploadedUrl))}
                                    title="Download file"
                                  >
                                    <DownloadIcon className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    type="text"
                                    size="small"
                                    onClick={() => removeFile(fieldId)}
                                    title="Remove file"
                                  >
                                    <XmarkIcon className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </Alert>
                          ) : status === 'error' ? (
                            // Error state
                            <div className="space-y-3">
                              <Alert variant="destructive">
                                <TriangleExclamationIcon className="h-4 w-4" />
                                <div className="flex items-center justify-between w-full">
                                  <div>
                                    <AlertDescription className="font-medium">
                                      Upload failed
                                    </AlertDescription>
                                    <AlertDescription className="text-xs mt-1">
                                      {errorMessage}
                                    </AlertDescription>
                                  </div>
                                </div>
                              </Alert>
                              {/* Show file input again for new file selection */}
                              <div
                                className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 p-6 text-center cursor-pointer hover:border-gray-400 transition-colors"
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onClick={() => {
                                  const input = document.createElement('input');
                                  input.type = 'file';
                                  input.onchange = (e) => {
                                    const selectedFile = (e.target as HTMLInputElement).files?.[0];
                                    if (selectedFile) handleFileInput(selectedFile);
                                  };
                                  input.click();
                                }}
                              >
                                <div className="flex flex-col items-center gap-2">
                                  <div className="w-8 h-8 bg-gray-300 rounded-lg flex items-center justify-center">
                                    <PlusIcon className="w-4 h-4 text-gray-500" />
                                  </div>
                                  <p className="text-xs text-gray-500">Choose a different file or drag and drop</p>
                                </div>
                              </div>
                            </div>
                          ) : status === 'getting-url' || status === 'uploading' ? (
                            // Loading state
                            <Alert className="border-blue-300 bg-blue-50">
                              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                              <div className="flex-1">
                                <AlertDescription className="text-sm font-medium text-gray-800">
                                  {file?.name}
                                </AlertDescription>
                                <Badge variant="secondary" className="bg-blue-100 text-blue-700 mt-1">
                                  {status === 'getting-url' ? 'Preparing upload...' : 'Uploading...'}
                                </Badge>
                                {status === 'uploading' && (
                                  <div className="mt-2 space-y-1">
                                    <Progress
                                      value={progress}
                                      className="w-full h-1.5"
                                      indicatorClassName="bg-blue-500"
                                    />
                                    <p className="text-xs text-blue-600">{progress}% uploaded</p>
                                  </div>
                                )}
                              </div>
                            </Alert>
                          ) : (
                            // Default state - file input
                            <div
                              className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 flex flex-col justify-start items-center gap-2 transition-colors cursor-pointer hover:bg-gray-100"
                              onDrop={handleDrop}
                              onDragOver={handleDragOver}
                              onClick={() => {
                                const input = document.createElement('input');
                                input.type = 'file';
                                input.onchange = (e) => {
                                  const selectedFile = (e.target as HTMLInputElement).files?.[0];
                                  if (selectedFile) handleFileInput(selectedFile);
                                };
                                input.click();
                              }}
                            >
                              <Button variant="outline" size="sm" className="bg-white border-gray-200 text-gray-800 hover:bg-gray-50">
                                <ArrowUpFromLineIcon className="w-3.5 h-3.5 mr-2" />
                                Upload File
                              </Button>
                              <p className="text-center text-gray-800 text-sm font-normal">
                                Select a file to upload
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    }

                    // Handle other field types
                    const fieldValue = formValues[schema.id];

                    return (
                      <div key={schema.id} className="relative h-12 rounded-lg border border-gray-200">

                        <label
                          className={`absolute left-3 transition-all duration-200 pointer-events-none
                            ${fieldValue && (Array.isArray(fieldValue) ? fieldValue.length > 0 : fieldValue)
                              ? 'text-xs text-gray-500 top-1'
                              : 'text-sm text-gray-500 top-3'}`}
                        >
                          {schema.name}{schema.isMandatory && <span className="text-red-400">*</span>}
                        </label>

                        {schema.type === FieldSchemaType.MULTI_SELECT ? (
                          <MultiSelect
                            options={schema.metaData?.options || []}
                            value={Array.isArray(fieldValue) ? fieldValue : []}
                            onChange={(value) => handleInputChange(schema.id, value)}
                            placeholder=""
                          />
                        ) : schema.type === FieldSchemaType.SINGLE_SELECT ? (
                          <SingleSelect
                            options={schema.metaData?.options || []}
                            value={fieldValue || ''}
                            onChange={(value) => handleInputChange(schema.id, value)}
                            placeholder=""
                          />
                        ) : schema.type === FieldSchemaType.BOOLEAN ? (
                          <SingleSelect
                            options={['Yes', 'No']}
                            value={fieldValue === 'true' ? 'Yes' : fieldValue === 'false' ? 'No' : ''}
                            onChange={(value) => handleInputChange(schema.id, value === 'Yes' ? 'true' : 'false')}
                            placeholder=""
                          />
                        ) : (
                          <div className="relative w-full h-full">
                            <input
                              type={schema.type === FieldSchemaType.DATE ? 'date' : 'text'}
                              value={fieldValue}
                              onChange={(e) => handleInputChange(schema.id, e.target.value)}
                              className={`w-full h-full bg-transparent border-0 focus:ring-0 focus:outline-none
                                ${fieldValue ? 'pt-5 pb-1' : 'px-3'} text-gray-800 text-sm
                                ${schema.type === FieldSchemaType.DATE ? 'pr-8' : ''}`}
                              placeholder=""
                              style={{
                                paddingLeft: '12px',
                                ...(schema.type === FieldSchemaType.DATE && {
                                  WebkitAppearance: 'none',
                                  MozAppearance: 'textfield',
                                  color: fieldValue ? 'inherit' : 'transparent'
                                })
                              }}
                            />
                            {schema.type === FieldSchemaType.DATE && (
                              <div
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
                                onClick={() => {
                                  const input = document.querySelector('input[type="date"]') as HTMLInputElement;
                                  if (input) {
                                    input.focus();
                                    input.showPicker?.();
                                  }
                                }}
                              >
                                <div className="flex items-center justify-center">
                                  <div className="w-4 h-4 relative">
                                    <svg
                                      className="w-4 h-4 absolute left-[1px] top-0 text-zinc-900"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                            )}
                            {schema.type === FieldSchemaType.DATE && (
                              <style jsx>{`
                                input[type="date"]::-webkit-calendar-picker-indicator {
                                  opacity: 0;
                                  position: absolute;
                                  right: 0;
                                  width: 100%;
                                  height: 100%;
                                  cursor: pointer;
                                }
                                input[type="date"]::-webkit-inner-spin-button,
                                input[type="date"]::-webkit-outer-spin-button {
                                  -webkit-appearance: none;
                                  margin: 0;
                                }
                                input[type="date"]::-webkit-datetime-edit {
                                  color: ${fieldValue ? 'inherit' : 'transparent'} !important;
                                }
                                input[type="date"]::-webkit-datetime-edit-fields-wrapper {
                                  color: ${fieldValue ? 'inherit' : 'transparent'} !important;
                                }
                                input[type="date"]::-webkit-datetime-edit-month-field,
                                input[type="date"]::-webkit-datetime-edit-day-field,
                                input[type="date"]::-webkit-datetime-edit-year-field {
                                  color: ${fieldValue ? 'inherit' : 'transparent'} !important;
                                }
                              `}
                              </style>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }) : null}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-white">

          <div className="flex gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={handleSubmit}
                    disabled={!areAllMandatoryFieldsFilled || updateLoading || hasActiveUploads}
                    className="h-8 px-3 py-1 bg-gray-800 rounded-lg text-white text-sm font-medium hover:bg-gray-900 disabled:opacity-50"
                  >
                    {updateLoading
                      ? 'Saving...'
                      : hasActiveUploads
                        ? 'Uploading files...'
                        : 'Submit'}
                  </Button>
                </TooltipTrigger>
                {!areAllMandatoryFieldsFilled && (
                <TooltipContent>
                  <p>Please fill in all required fields to submit the form</p>
                </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={updateLoading}
              className="h-8 px-3 py-1 bg-white rounded-lg border border-gray-200 text-gray-800 text-sm font-medium"
            >
              Cancel
            </Button>
          </div>
          {updateError && (
            <p className="text-red-500 text-sm mt-2">
              Failed to save changes. Please try again.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
